import { registerIndicator } from 'klinecharts';

export const PIVOT_INDICATOR_NAME = 'PIVOT';
export const PIVOT_CHART_TYPE_LINE = 'area';

let registered = false;

export function registerPivotIndicator(): void {
   if (registered) {
      return;
   }
   registered = true;

   const ARROW_GAP_PX = 6;
   const ARROW_HEIGHT_RATIO = 2.0;

   const drawTriangles = (params: any): boolean => {
      const ctx = params?.ctx;
      const indicator = params?.indicator;
      const xAxis = params?.xAxis;
      const yAxis = params?.yAxis;
      const visibleRange = params?.visibleRange;
      const barSpace = params?.barSpace;
      if (!ctx || !indicator || !xAxis || !yAxis) {
         return false;
      }

      const result: Array<{ pivotUp?: number; pivotDown?: number } | undefined> = indicator.result || [];
      const upColor: string = indicator?.styles?.circles?.[0]?.upColor || '#26a69a';
      const downColor: string = indicator?.styles?.circles?.[1]?.upColor || '#ef5350';
      const halfW = Math.max(3, ((barSpace?.halfBar) || 4) * 0.85);
      const h = halfW * ARROW_HEIGHT_RATIO;

      const fromIdx = Math.max(0, (visibleRange?.from ?? 0) - 1);
      const toIdx = Math.min(result.length, (visibleRange?.to ?? result.length) + 1);

      for (let i = fromIdx; i < toIdx; i++) {
         const r = result[i];
         if (!r) {
            continue;
         }
         const x = xAxis.convertToPixel(i);

         if (Number.isFinite(r.pivotUp)) {
            const y = yAxis.convertToPixel(r.pivotUp as number);
            ctx.fillStyle = upColor;
            ctx.beginPath();
            ctx.moveTo(x - halfW, y - ARROW_GAP_PX);
            ctx.lineTo(x + halfW, y - ARROW_GAP_PX);
            ctx.lineTo(x, y - ARROW_GAP_PX - h);
            ctx.closePath();
            ctx.fill();
         }

         if (Number.isFinite(r.pivotDown)) {
            const y = yAxis.convertToPixel(r.pivotDown as number);
            ctx.fillStyle = downColor;
            ctx.beginPath();
            ctx.moveTo(x - halfW, y + ARROW_GAP_PX);
            ctx.lineTo(x + halfW, y + ARROW_GAP_PX);
            ctx.lineTo(x, y + ARROW_GAP_PX + h);
            ctx.closePath();
            ctx.fill();
         }
      }
      return false;
   };

   const template: any = {
      name: PIVOT_INDICATOR_NAME,
      shortName: 'Pivots',
      series: 'price',
      calcParams: ['candle_solid'],
      precision: 2,
      shouldOhlc: true,
      figures: [
         {
            key: 'pivotUp',
            title: 'Pivot↑: ',
            type: 'circle',
            attrs: (params: any) => {
               const x = params?.coordinate?.current?.x ?? -100;
               const y = params?.coordinate?.current?.pivotUp ?? -100;
               return { x, y, r: 0 };
            },
            styles: (_data: any, indicator: any, defaultStyles: any) => {
               const color = indicator?.styles?.circles?.[0]?.upColor
                  || defaultStyles?.circles?.[0]?.upColor
                  || '#26a69a';
               return { style: 'fill', color, borderColor: color };
            }
         },
         {
            key: 'pivotDown',
            title: 'Pivot↓: ',
            type: 'circle',
            attrs: (params: any) => {
               const x = params?.coordinate?.current?.x ?? -100;
               const y = params?.coordinate?.current?.pivotDown ?? -100;
               return { x, y, r: 0 };
            },
            styles: (_data: any, indicator: any, defaultStyles: any) => {
               const color = indicator?.styles?.circles?.[1]?.upColor
                  || defaultStyles?.circles?.[0]?.downColor
                  || '#ef5350';
               return { style: 'fill', color, borderColor: color };
            }
         }
      ],
      draw: drawTriangles,
      calc: (dataList: any[], indicator: any) => {
         const params: any[] = indicator?.calcParams || [];
         const chartType: string = params[0] || 'candle_solid';
         const useClose = chartType === PIVOT_CHART_TYPE_LINE;

         const out: Array<{ pivotUp?: number; pivotDown?: number }> = new Array(dataList.length);
         for (let i = 0; i < dataList.length; i++) {
            const d = dataList[i];
            const prev = dataList[i - 1];
            const next = dataList[i + 1];
            const r: { pivotUp?: number; pivotDown?: number } = {};
            if (d && prev && next) {
               if (useClose) {
                  if (d.close > prev.close && d.close > next.close) {
                     r.pivotUp = d.close;
                  }
                  if (d.close < prev.close && d.close < next.close) {
                     r.pivotDown = d.close;
                  }
               } else {
                  if (d.high > prev.high && d.high > next.high) {
                     r.pivotUp = d.high;
                  }
                  if (d.low < prev.low && d.low < next.low) {
                     r.pivotDown = d.low;
                  }
               }
            }
            out[i] = r;
         }
         return out;
      }
   };

   registerIndicator(template);
}
